import cn from "@/v2-ui/utils/utils.cn"
import Icon, { type IconProps } from "@/v2-ui/icon"
import {
  Button as ButtonBase,
  type ButtonProps as ButtonBaseProps,
} from "./base/ButtonBase"

export type ButtonProps = ButtonBaseProps & {
  iconPosition?: "left" | "right"
  iconSize?: IconProps["size"]
  iconSrc?: IconProps["src"]
  iconBackdrop?: IconProps["iconBackdrop"]
  iconStyle?: IconProps["style"]
  iconClassName?: IconProps["className"]
  iconInheritSize?: IconProps["inheritSize"]
  isLoading?: boolean
}

function Button(props: ButtonProps) {
  const {
    className,
    iconPosition = "left",
    iconSrc,
    iconSize,
    iconBackdrop,
    iconStyle,
    iconClassName,
    iconInheritSize,
    isLoading,
    children,
    ...buttonProps
  } = props

  const isIconShown = Boolean(iconSrc || isLoading)

  // Radix Slot (asChild) requires exactly one element child, but the JSX
  // below always emits an [icon, children, icon] array (even when the icon
  // slots are null), which makes React.Children.only throw inside the Slot.
  // With asChild the caller owns the child markup, so pass it through as-is —
  // meaning iconSrc/isLoading are deliberately IGNORED here: render your own
  // icon/spinner inside the child when combining them with asChild.
  if (buttonProps.asChild) {
    return (
      <ButtonBase className={cn("ui-button", className)} {...buttonProps}>
        {children}
      </ButtonBase>
    )
  }

  return (
    <ButtonBase
      className={cn("ui-button", className)}
      disabled={isLoading}
      {...buttonProps}
    >
      {iconPosition === "left" && isIconShown ? (
        <Icon
          src={iconSrc}
          size={iconSize}
          loading={isLoading}
          iconBackdrop={iconBackdrop}
          style={iconStyle}
          className={iconClassName}
          inheritSize={iconInheritSize}
        />
      ) : null}
      {children}
      {iconPosition === "right" && isIconShown ? (
        <Icon
          src={iconSrc}
          size={iconSize}
          loading={isLoading}
          iconBackdrop={iconBackdrop}
          style={iconStyle}
          className={iconClassName}
          inheritSize={iconInheritSize}
        />
      ) : null}
    </ButtonBase>
  )
}

export default Button
